import React, { useRef, useState } from "react";

function VideoRecorder({ onRecordingComplete }) {
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const recordedChunks = useRef([]);
  const gainNodeRef = useRef(null);
  const [volume, setVolume] = useState(1);

  const startRecording = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: false,
      },
    });

    const audioContext = new (window.AudioContext ||
      window.webkitAudioContext)();
    const source = audioContext.createMediaStreamSource(stream);
    const gainNode = audioContext.createGain();
    gainNode.gain.value = volume;
    gainNodeRef.current = gainNode;

    source.connect(gainNode);
    const dest = audioContext.createMediaStreamDestination();
    gainNode.connect(dest);

    const combinedStream = new MediaStream([
      ...stream.getVideoTracks(),
      ...dest.stream.getAudioTracks(),
    ]);

    videoRef.current.srcObject = combinedStream;
    videoRef.current.play();

    mediaRecorderRef.current = new MediaRecorder(combinedStream);
    recordedChunks.current = [];

    mediaRecorderRef.current.ondataavailable = (event) => {
      if (event.data.size > 0) {
        recordedChunks.current.push(event.data);
      }
    };

    mediaRecorderRef.current.onstop = () => {
      const blob = new Blob(recordedChunks.current, { type: "video/webm" });
      const url = URL.createObjectURL(blob);
      setVideoUrl(url);
      if (onRecordingComplete) {
        onRecordingComplete(blob);
      }
    };

    mediaRecorderRef.current.start();
    setRecording(true);
  };

  const stopRecording = () => {
    mediaRecorderRef.current.stop();
    setRecording(false);

    const tracks = videoRef.current.srcObject.getTracks();
    tracks.forEach((track) => track.stop());
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = newVolume;
    }
  };

  const handlePlay = () => {
    if (videoRef.current && videoUrl) {
      videoRef.current.srcObject = null;
      videoRef.current.src = videoUrl;
      videoRef.current.play();
    }
  };

  const handleReset = () => {
    setVideoUrl(null);
    videoRef.current.src = "";
    videoRef.current.srcObject = null;
    setRecording(false);
  };

  const buttonStyle = {
    padding: "10px 16px",
    margin: "5px",
    fontSize: "16px",
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
    backgroundColor: "#1976d2",
    color: "white",
  };

  const stopButtonStyle = {
    ...buttonStyle,
    backgroundColor: "#d32f2f",
  };

  const videoStyle = {
    width: "100%",
    maxWidth: "500px",
    borderRadius: "12px",
    border: "2px solid #ccc",
    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
  };
  const btn = (color, isLink = false) => ({
    padding: "6px 10px",
    fontSize: "14px",
    borderRadius: "6px",
    border: "none",
    textAlign: "center",
    textDecoration: "none",
    backgroundColor: {
      blue: "#1976d2",
      red: "#d32f2f",
      green: "#388e3c",
      gray: "#555",
      orange: "#f57c00",
    }[color],
    color: "white",
    cursor: "pointer",
    display: isLink ? "inline-block" : "block",
  });

  return (
    <div
      style={{
        padding: 20,
        display: "flex",
        gap: 20,
        alignItems: "flex-start",
      }}
    >
      {/* Video Section */}
      <div>
        <video
          ref={videoRef}
          style={{
            width: "320px",
            height: "240px",
            borderRadius: "10px",
            border: "1px solid #ccc",
            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
          }}
          controls
          muted
        />

        <div style={{ marginTop: 8 }}>
          <label style={{ fontSize: "14px" }}>Mic Volume:</label>
          <input
            type="range"
            min="0"
            max="2"
            step="0.01"
            value={volume}
            onChange={handleVolumeChange}
            style={{ width: "100%" }}
          />
        </div>
      </div>

      {/* Button Section */}
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {!recording ? (
          <button onClick={startRecording} style={btn("blue")}>
            Start
          </button>
        ) : (
          <button onClick={stopRecording} style={btn("red")}>
            Stop
          </button>
        )}
        {videoUrl && !recording && (
          <>
            <button onClick={handlePlay} style={btn("gray")}>
              Play
            </button>
            <button onClick={handleReset} style={btn("orange")}>
              Reset
            </button>
            <a
              href={videoUrl}
              download="recorded-video.webm"
              style={btn("green", true)}
            >
              Save
            </a>
          </>
        )}
      </div>
    </div>
  );
}

export default VideoRecorder;
